import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth';

type UserRow = {
  id: number;
  name: string;
  email: string;
  role: string;
  about_me: string | null;
  created_at: string | null;
  approved_at: string | null;
  last_login_at: string | null;
  pending_submissions: number;
  archives: number;
};

export default function AdminUsers() {
  const { refreshReviewCounts } = useAuth();
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  async function load() {
    const { data } = await api<{ data: UserRow[] }>('/admin/users');
    setUsers(data);
  }

  useEffect(() => { load(); }, []);

  async function approve(id: number) {
    if (!window.confirm('Approve this user? They\'ll be able to view all archives immediately.')) return;
    setBusy(id);
    try {
      await api(`/admin/users/${id}/approve`, { method: 'POST' });
      await load();
      await refreshReviewCounts();
    } finally {
      setBusy(null);
    }
  }

  if (users === null) {
    return <div className="card"><p className="muted">Loading…</p></div>;
  }

  const pending = users.filter((u) => u.approved_at === null);

  return (
    <>
      {pending.length > 0 && (
        <div className="card">
          <h2>Awaiting approval ({pending.length})</h2>
          <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
            Newest registrations first. Approve to grant archive-viewing access;
            promotion to admin still needs a manual DB update.
          </p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Registered</th>
                <th>About</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((u) => (
                <tr key={u.id}>
                  <td><Link to={`/admin/users/${u.id}`}>{u.name}</Link></td>
                  <td>{u.email}</td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ wordBreak: 'break-word', maxWidth: 400 }}>
                    {u.about_me ?? <span className="muted">—</span>}
                  </td>
                  <td>
                    <button onClick={() => approve(u.id)} disabled={busy === u.id}>
                      {busy === u.id ? 'Approving…' : 'Approve'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h2>All users ({users.length})</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Per-user counts cover both the direct-create and submission-approval
          paths. "Submissions" is the count currently awaiting review; they
          flip into the archives column once approved (or quietly disappear
          on rejection). Click a name for the full profile + activity log.
        </p>
        {users.length === 0 ? (
          <p className="muted">No users yet.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Archives</th>
                <th style={{ textAlign: 'right' }}>Pending submissions</th>
                <th>Joined</th>
                <th>Approved</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td><Link to={`/admin/users/${u.id}`}>{u.name}</Link></td>
                  <td>{u.email}</td>
                  <td>
                    {u.role === 'admin'
                      ? <span className="status-pill admin" style={{ background: 'var(--pill-bg-retry)', color: 'var(--pill-fg-retry)' }}>admin</span>
                      : <span className="muted">user</span>}
                  </td>
                  <td>
                    {u.approved_at
                      ? <span className="muted">approved</span>
                      : <span style={{ color: '#a60' }}>pending</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>{u.archives}</td>
                  <td style={{ textAlign: 'right' }}>
                    {u.pending_submissions > 0
                      ? <strong>{u.pending_submissions}</strong>
                      : <span className="muted">0</span>}
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {u.approved_at ? new Date(u.approved_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
